/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import avatars from "../../assets/images/Avatars/avatars";
import Typography from "../../components/common/text/Typography";
import Iconify from "../../components/common/icon/Iconify";
import CrystalButton from "../../assets/images/buttonIcons/Crystal-button-BG.png";
import UserNameIcon from "../../assets/images/icons/username.svg";
import { setColor } from "../../assets/styles/styles";
import { Input } from "../../components/common/input/Input";
import Scrollbar from "../../components/Scrollbar";
import { validateUsername } from "../../utils/generalUtils";
import { getFromSessionStorage, setToSessionStorage } from "../../hooks/useSessionStorage";
import { usernameExistsChecker } from "../../api/UserController";
import AvatarBackground from "../../assets/images/backgrounds/UI_BG_Avatar_Selection.png";
import { sendSocketMessage } from "../../socket";
import { fetchVanityLinkAvatarPresets } from "../../api/VanityLinkController";

const VanityAvatarPage = ({
    username,
    setUsername,
    setCurrentScreen,
    handleRegisterSubmit,
    noUsername,
    source,
    setIsAvatarOpen,
    vanityLink,
}) => {
    const [selectedAvatar, setSelectedAvatar] = useState(null);
    const [orientation, setOrientation] = useState(window.innerWidth > window.innerHeight ? "landscape" : "portrait");
    const [invalidUsernameError, setInvalidUsernameError] = useState("");
    const [presetAvatarNames, setPresetAvatarNames] = useState([]);
    const [isLoadingAvatars, setIsLoadingAvatars] = useState(true);
    const userId = useSelector((state) => state.user?.userId);

    // Get the vanity link from URL if not passed as prop
    const pathId = vanityLink || window.location.pathname.split("/").pop();
    // console.log("[VanityAvatarPage] pathId:", pathId);

    // Filter avatars based on presetAvatarNames from vanity link data
    const filteredAvatars = useMemo(() => {
        // console.log("[VanityAvatarPage] presetAvatarNames:", presetAvatarNames);
        if (presetAvatarNames.length === 0) {
            // If no preset avatars configured, show all avatars
            console.log("[VanityAvatarPage] No preset avatars, showing all avatars");
            return avatars;
        }
        const filtered = avatars.filter((avatar) => presetAvatarNames.includes(avatar.name));
        // console.log(
        //     "[VanityAvatarPage] Filtered avatars count:",
        //     filtered.length,
        //     filtered.map((a) => a.name)
        // );
        return filtered;
    }, [presetAvatarNames]);

    // Fetch vanity link avatar presets
    useEffect(() => {
        const loadVanityLinkAvatarPresets = async () => {
            if (!pathId) {
                console.log("[VanityAvatarPage] No pathId, skipping fetch");
                setIsLoadingAvatars(false);
                return;
            }

            try {
                console.log("[VanityAvatarPage] Fetching avatar presets for pathId:", pathId);
                const response = await fetchVanityLinkAvatarPresets(pathId);
                console.log("[VanityAvatarPage] Avatar presets response:", response);

                if (response?.data?.avatarPresets && response.data.avatarPresets.length > 0) {
                    // Extract the avatar names from the response
                    const avatarNames = response.data.avatarPresets.map((avatar) => {
                        // console.log("[VanityAvatarPage] Avatar preset item:", avatar.name);
                        return avatar.name;
                    });
                    // console.log("[VanityAvatarPage] Extracted avatarNames:", avatarNames);
                    setPresetAvatarNames(avatarNames);
                } else {
                    console.log("[VanityAvatarPage] No avatarPresets in response, showing all avatars");
                }
            } catch (error) {
                console.error("[VanityAvatarPage] Error fetching avatar presets:", error);
            } finally {
                setIsLoadingAvatars(false);
            }
        };

        loadVanityLinkAvatarPresets();
    }, [pathId]);

    // Set default selected avatar once filtered avatars are available
    useEffect(() => {
        if (!isLoadingAvatars && filteredAvatars.length > 0 && !selectedAvatar) {
            // Check if there's a saved avatar in session storage
            const savedAvatarName = getFromSessionStorage("selectedAvatar");
            if (savedAvatarName) {
                const savedAvatar = filteredAvatars.find((avatar) => avatar.name === savedAvatarName);
                if (savedAvatar) {
                    setSelectedAvatar(savedAvatar);
                }
                // If saved avatar not found in filtered list, selectedAvatar stays null
            }
            // No fallback - selectedAvatar stays null if nothing in session storage
        }
    }, [isLoadingAvatars, filteredAvatars, selectedAvatar]);

    const handleRegistration = async () => {
        if (noUsername && source !== "editAvatarModal") {
            setCurrentScreen("vanityRedirect");
        }
        if (source === "editAvatarModal") {
            // console.log("Selected Avatar to send from VANITY:", selectedAvatar?.name, userId);
            sendSocketMessage("RPMAvatarEvent", "", userId, "LOAD_RPM_AVATAR", {
                data: selectedAvatar?.name,
            });
            setIsAvatarOpen?.(false);
        }
        if (
            username &&
            invalidUsernameError === "" &&
            username.length > 2 &&
            !noUsername &&
            source !== "editAvatarModal"
        ) {
            await handleRegisterSubmit();
        } else {
            setInvalidUsernameError("Please enter a valid username before proceeding.");
        }
    };

    useEffect(() => {
        usernameExistsChecker(username).then((res) => {
            if (res) {
                // Username exists
                setInvalidUsernameError("Username already exists, please try a unique username.");
            } else if (!validateUsername(username) && username.length > 2) {
                // Invalid username
                setInvalidUsernameError(
                    "Username must start with a letter, can only contain letters, numbers, underscores, or hyphens, and be between 3 and 20 characters long."
                );
            } else {
                // Username is valid
                setInvalidUsernameError("");
            }
        });
    }, [username]);

    const handleAvatarClick = (avatar) => {
        setSelectedAvatar(avatar);
        // console.log('selectedAvatar', avatar.name);
        setToSessionStorage("selectedAvatar", avatar.name);
    };

    const handleBack = () => {
        if (noUsername && source !== "editAvatarModal") {
            window.location.reload();
        } else if (source === "editAvatarModal") {
            setIsAvatarOpen(false);
        } else {
            setCurrentScreen("vanityEmailPage");
        }
    };

    useEffect(() => {
        const handleResize = () => {
            setOrientation(window.innerWidth > window.innerHeight ? "landscape" : "portrait");
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const isRpm = (avatarName) => avatarName?.startsWith("RPM_");

    return (
        <Container orientation={orientation}>
            <Iconify
                style={{
                    position: "absolute",
                    top: "20px",
                    left: "20px",
                    height: "50px",
                    zIndex: 1000,
                    cursor: "pointer",
                }}
                Color={setColor.accent4}
                onClick={handleBack}
                icon="material-symbols:arrow-back-ios"
            />
            <AvatarSelector>
                <Scrollbar sx={{ margin: "20px" }}>
                    <Typography style={{ fontWeight: 500, fontSize: "22px", marginTop: "0rem" }} variant={"pWhite"}>
                        {noUsername
                            ? "Lastly, choose your Avatar"
                            : noUsername && source !== "editAvatarModal"
                              ? "Choose your Avatar"
                              : "Lastly, choose your username and avatar!"}
                    </Typography>
                    {!noUsername && (
                        <Input
                            variant="LoginInput"
                            type="name"
                            value={username}
                            maxLength={19}
                            borderRadius="0px"
                            containerStyle={{
                                height: "60px",
                                width: "100%",
                                paddingLeft: "10px",
                            }}
                            placeholder="Enter your username"
                            onChangeHandler={(e) => setUsername(e.target.value)}
                            inputIconColor={invalidUsernameError === "" && username.length > 2 ? "#92ECC7" : "grey"}
                            customIcon={UserNameIcon}
                            checkMarkIcon={"carbon:checkmark-filled"}
                        />
                    )}
                    {invalidUsernameError && (
                        <p style={{ marginBottom: "25px", color: setColor.errorColor }}>{invalidUsernameError}</p>
                    )}
                    {isLoadingAvatars ? (
                        <div
                            style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "40px" }}
                        >
                            <Typography variant="pWhite">Loading avatars...</Typography>
                        </div>
                    ) : (
                        <AvatarGrid>
                            {filteredAvatars.map((avatar) => (
                                <AvatarThumbnailContainer
                                    isSelected={avatar.id === selectedAvatar?.id}
                                    key={avatar.id}
                                    onClick={() => handleAvatarClick(avatar)}
                                >
                                    <AvatarThumbnail
                                        isRpmAvatar={isRpm(avatar.name)}
                                        isSelected={avatar.id === selectedAvatar?.id}
                                        style={{ backgroundImage: `url(${avatar.image})` }}
                                    />
                                </AvatarThumbnailContainer>
                            ))}
                        </AvatarGrid>
                    )}
                </Scrollbar>
            </AvatarSelector>
            <AvatarPreview>
                <Typography
                    style={{
                        fontWeight: 500,
                        fontSize: "20px",
                        color: "#E4DEFF",
                        wordBreak: "break-all",
                        margin: "0",
                        padding: "0",
                        minHeight: "32px",
                    }}
                    variant={"pWhite"}
                >
                    {username}
                </Typography>
                {!isLoadingAvatars && selectedAvatar && (
                    <SelectedAvatar
                        isRpmAvatar={isRpm(selectedAvatar?.name)}
                        orientation={orientation}
                        src={selectedAvatar?.fullbody || selectedAvatar?.image}
                        alt="Selected Avatar"
                    />
                )}
                <JumpInButton onClick={handleRegistration}>Jump In</JumpInButton>
            </AvatarPreview>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    height: 100vh;
    width: 100%;
    backdrop-filter: blur(100px);
    z-index: 100000;
    background: linear-gradient(73deg, rgba(11, 11, 44, 0.75) 0%, rgba(33, 13, 53, 0.75) 98.54%);

    ${(props) => {
        if (props.orientation && props.orientation === "portrait") {
            return `
        transform: rotate(-90deg);
        height:100vw;
        width: 100vh;
      `;
        }
        return `
        transform: none;
        height: 100%;
        width: 100%;
      `;
    }}
`;

const SelectedAvatar = styled.img`
    height: ${(props) => (props.isRpmAvatar ? "70vh" : "100vh")};
    /* ${(props) => props.isRpmAvatar && "width: fill;"}; */
    flex: 1;

    ${(props) => {
        if (props.orientation) {
            return `height:70%;`;
        }
    }}

    @media (max-width: 960px) {
        flex: 0;
        margin: 5px 0;
    }
`;

const AvatarSelector = styled.div`
    flex: 3;
    overflow-y: auto;
    padding: 20px 20px 0 20px;
    width: 80%;
`;

const AvatarGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 40px;
    margin-top: 2.3rem;
    padding-bottom: 2.5rem;

    @media (max-width: 960px) {
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;
    }
`;

const AvatarThumbnailContainer = styled.div`
    width: 100%;
    padding-top: 100%;
    position: relative;
    overflow: hidden;
    backdrop-filter: blur(100px);
    background: ${(props) => (props.isSelected ? `rgba(11, 11, 44, 0.75);` : "rgba(190, 174, 255, 0.1);")};
    border-radius: 5px;
`;

const AvatarThumbnail = styled.div`
    background-size: ${(props) => (props.isRpmAvatar ? "120% auto" : "240% auto")};
    background-position: center top;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
    border: ${(props) => (props.isSelected ? `4px solid ${setColor.iconColor3}}` : "none")};
    transition: transform 0.2s ease-in-out;
`;

const AvatarPreview = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    padding: 45px 20px 0 20px;
    width: 30vw;
    height: 100vh;
    background-image: url(${AvatarBackground});
    background-size: cover;
    border-left: 3px solid rgba(190, 174, 255, 0.2);

    @media (max-width: 960px) {
        height: 100%;
        padding: 15px;
        margin: 0;
        justify-content: center;
    }

    @media (max-width: 425px) {
        width: 30vh;
    }
`;

const JumpInButton = styled.button`
    margin-top: 35px;
    width: 100%;
    border: none;
    height: 65px;
    flex-shrink: 0;
    background: url(${CrystalButton});
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    cursor: pointer;
    color: #1b1b23;

    text-align: center;
    font-family: Barlow;
    font-size: 26px;
    font-style: normal;
    font-weight: 800;
    letter-spacing: 3.6px;
    text-transform: uppercase;

    @media (max-width: 960px) {
        font-size: 16px;
        letter-spacing: 1.8px;
        margin-top: 0;
        height: 38px;
    }
`;

export default VanityAvatarPage;
